import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
} from "@angular/core";
import * as Highcharts from "highcharts";
import { AppSettings } from "../../app-settings";

/**
 * Represents an activity type in the time tracking system
 */
interface ActivityType {
  /** Unique identifier for the activity type */
  id: string;

  /** Display name of the activity type */
  name: string;

  /** Color to use for this activity in the chart */
  color: string;

  /** Optional maximum hours for this activity type */
  maxHours?: number;

  /** Whether the activity is visible in the chart */
  visible?: boolean;
}

/**
 * Represents a time entry for a specific date with activity breakdown
 */
interface TimeEntry {
  /** Date of the time entry */
  date: Date;

  /** List of activities performed on this date */
  activities: {
    /** Reference to the activity type */
    activityTypeId: string;

    /** Time spent on this activity in minutes */
    minutes: number;
  }[];

  /** Target time commitment for this date in minutes */
  committedTimeMinutes: number;

  /** Whether the daily goal was achieved */
  goalAchieved: boolean;
  /** Whether this is a no-study date */
  noStudyDate?: boolean;
}

@Component({
  selector: "app-graph-time-tracking-highchart",
  templateUrl: "./graph-time-tracking-highchart.component.html",
  styleUrls: ["./graph-time-tracking-highchart.component.scss"],
})
export class GraphTimeTrackingHighchartComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild("chartContainer", { static: false }) chartContainer: ElementRef;

  iconCommitedTime = AppSettings.cdnUrl + "assets/images/ico-legend.svg";
  iconNoStudy = AppSettings.cdnUrl + "assets/images/ico-no study.svg";

  totalStudyMinutes: number = 0;
  chart: any; // Highcharts.Chart;
  Highcharts = Highcharts; // This needs to be a property, not a type
  chartOptions: any = {}; // Highcharts.Options
  updateFlag = false;

  @Output() CalculateTotalTimeSpent: EventEmitter<any> = new EventEmitter();

  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chart = chart;
  };

  @Input() days: number = 8; // Default to 8 days
  @Input() trackingData: any; // Input for the tracking data

  // Internal data storage
  timeEntries: TimeEntry[] = [];
  activityTypes: ActivityType[] = [];
  originalSeriesData: any[] = []; // Store the original series data
  committedTimeSeries: any; // Store the committed time series separately

  // Threshold for considering an activity value as significant (in hours)
  private readonly ACTIVITY_THRESHOLD = 0; // 0.05 hours (3 minutes)

  constructor() {}

  private countGoalAchievements() {
    // Total goals is the total number of days
    const totalGoals = this.timeEntries.length;

    // Achieved goals is the count of entries where goalAchieved is true
    const achievedGoals = this.timeEntries.filter(
      (entry) => entry.goalAchieved
    ).length;

    return { totalGoals, achievedGoals };
  }

  renderNoStudyIndicators(): void {
    if (!this.chart) {
      console.warn("Cannot render no-study indicators: chart not available");
      return;
    }

    // Only proceed if all activities are visible
    if (!this.activityTypes.every((act) => act.visible)) {
      return;
    }

    // Loop through the time entries
    this.timeEntries.forEach((entry, index) => {
      if (entry.noStudyDate) {
        // For No Study days, place the icon at the same vertical position as the goal icon
        const xPos = this.chart.xAxis[0].toPixels(index, false);

        // Use the exact same positioning logic as the goal achievement icon
        // Find all visible column series
        const columnSeries = this.chart.series.filter(
          (s) =>
            s.visible &&
            s.options.type === "column" &&
            s.points &&
            s.points.length > index
        );

        // Get the top position
        let topY = this.chart.plotHeight; // Start with plot height

        // Determine the vertical position - match the goal icon exactly
        if (columnSeries.length > 0) {
          // Find the top of the stack for this day
          columnSeries.forEach((series) => {
            if (series.points[index] && series.points[index].y > 0) {
              const point = series.points[index];
              if (point.plotY < topY) {
                topY = point.plotY;
              }
            }
          });
        } else {
          // If no columns, use a default position
          topY = this.chart.plotTop + 20;
        }

        // Calculate position - use the exact same formula as goal icons
        const yPos = Math.max(topY - 15, this.chart.plotTop + 10);

        // Adjust horizontal position if both goal and no-study exist
        const hasGoalAchievement = entry.goalAchieved;
        const adjustedXPos = hasGoalAchievement ? xPos + 20 : xPos; // Larger offset to avoid overlap

        // Remove any existing icon with the same id
        const existingIcon = document.getElementById(`no-study-${index}`);
        if (existingIcon) existingIcon.remove();

        // Add the no-study icon using an image
        this.chart.renderer
          .image(
            this.iconNoStudy, // Use the image URL
            adjustedXPos - 13, // Center the image (assuming 26X26 image)
            yPos - 13,
            26, // Width of image
            26 // Height of image
          )
          .attr({
            zIndex: 10,
            id: `no-study-${index}`,
          })
          .add();
      }
    });
  }

  renderGoalAchievements(): void {
    if (!this.chart) {
      console.warn("Cannot render goal achievements: chart not available");
      return;
    }

    // Only proceed if all activities are visible
    if (!this.activityTypes.every((act) => act.visible)) {
      return;
    }

    // Get goal achievements data
    const goalAchievements = this.timeEntries.map(
      (entry) => entry.goalAchieved
    );

    // Loop through the goal achievements data
    goalAchievements.forEach((achieved, index) => {
      if (achieved) {
        // Find the top of the stack for this date
        let topY = this.chart.plotHeight; // Start with a large value

        const columnSeries = this.chart.series.filter(
          (s) => s.visible && s.options.type === "column"
        );

        if (columnSeries.length > 0) {
          // Find the top of the stack for this day
          columnSeries.forEach((series) => {
            if (series.points[index] && series.points[index].y > 0) {
              const point = series.points[index];
              if (point.plotY < topY) {
                topY = point.plotY;
              }
            }
          });
        } else {
          // If no columns, use a default position
          topY = this.chart.plotTop + 20;
        }

        // Calculate position - use the exact same formula as goal icons
        const yPos = Math.max(topY - 15, this.chart.plotTop + 10);

        // Calculate position - place just 15px above the highest point
        const xPos = this.chart.xAxis[0].toPixels(index, false);

        // Remove any existing marker with the same id
        const existingCircle = document.getElementById(`goal-circle-${index}`);
        const existingCheck = document.getElementById(`goal-check-${index}`);

        if (existingCircle) existingCircle.remove();
        if (existingCheck) existingCheck.remove();

        // Add the green circle
        this.chart.renderer
          .circle(xPos, yPos, 8)
          .attr({
            fill: "#4CAF50",
            zIndex: 10,
            id: `goal-circle-${index}`,
          })
          .add();

        // Add the checkmark as a path
        this.chart.renderer
          .path()
          .attr({
            d: [
              "M",
              xPos - 3,
              yPos,
              "L",
              xPos - 1,
              yPos + 2,
              "L",
              xPos + 3,
              yPos - 3,
            ],
            stroke: "#FFFFFF",
            "stroke-width": 1.5,
            zIndex: 11,
            id: `goal-check-${index}`,
          })
          .add();
      }
    });
  }

  formatTimeFromMinutes(totalMinutes: number): string {
    // Calculate hours and remaining minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);

    // Handle edge case where rounding minutes results in 60
    if (minutes === 60) {
      return `${hours + 1}h 0m`;
    }

    // Return formatted string
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  ngOnInit(): void {
    // Process tracking data if available
    if (this.trackingData) {
      this.processTrackingData();
      this.prepareChartOptions();
    } else {
      console.warn("No tracking data available on init");
    }
  }

  ngAfterViewInit(): void {
    // Delay chart initialization to ensure DOM is ready
    setTimeout(() => {
      this.updateFlag = true;

      // Use a short timeout to get the chart reference after it's been created
      setTimeout(() => {
        // Get the chart instance after it's created
        const chartEl = document.querySelector(".highcharts-container");
        if (chartEl) {
          chartEl.setAttribute(
            "style",
            "width: 100% !important; height: 350px !important;"
          );
        } else {
          console.warn(
            "No highcharts-container found in DOM after initialization"
          );
        }

        if (this.chart) {
          this.chart.reflow();
        } else {
          console.warn("No chart reference available after initialization");
        }

        setTimeout(() => {
          this.replaceTopStacksWithRoundedPath();
        }, 300);
      }, 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Ensure chart is properly cleaned up when component is destroyed
    this.destroyChart();
  }

  // Add this method to properly handle chart instance
  private destroyChart(): void {
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (e) {
        console.warn("Error destroying chart:", e);
      }
      this.chart = null;
    }
  }

  /**
   * Toggle activity visibility when checkbox is clicked
   */
  toggleActivity(activity: ActivityType): void {
    // Update the activity visibility state
    activity.visible = !activity.visible;

    // We need to rebuild the series data to ensure proper stacking
    if (this.chart) {
      try {
        // Check if all activities are now visible or not
        const allActivitiesVisible = this.activityTypes.every(
          (act) => act.visible
        );

        // Remove all current series
        while (this.chart.series.length > 0) {
          this.chart.series[0].remove(false); // false = don't redraw yet
        }

        // Rebuild series from original data, only including visible activities
        // and only including committed time line if all activities are visible
        const newSeries = this.originalSeriesData
          .filter((seriesData) => {
            if (seriesData.type === "line" || seriesData.type === "spline") {
              // Only include committed time if all activities are visible
              return allActivitiesVisible;
            } else {
              // For column series, check if the activity is visible
              const activityType = this.activityTypes.find(
                (a) => a.name === seriesData.name
              );
              return activityType && activityType.visible;
            }
          })
          .map((seriesData) => ({ ...seriesData }));

        // Check if we have any series to add
        if (newSeries.length === 0) {
          console.warn("No series data available to add after filtering");
        }

        // Add filtered series back to the chart
        newSeries.forEach((seriesData) => {
          this.chart.addSeries(seriesData, false); // false = don't redraw yet
        });

        // Redraw the chart once with all changes
        this.chart.redraw();

        // Important: Only render goal icons if all activities are visible
        if (this.chart.renderer) {
          // Clear any existing goal icons first
          for (let i = 0; i < this.timeEntries.length; i++) {
            const existingCircle = document.getElementById(`goal-circle-${i}`);
            const existingCheck = document.getElementById(`goal-check-${i}`);
            if (existingCircle) existingCircle.remove();
            if (existingCheck) existingCheck.remove();
          }

          // Only render goal icons if all activities are visible
          if (allActivitiesVisible) {
            // Wait for chart to update before drawing goal icons
            setTimeout(() => {
              const goalStats = this.countGoalAchievements();
              if (goalStats.achievedGoals > 0) {
                // Manually render the goal achievement icons
                this.renderGoalAchievements();
              }

              this.renderNoStudyIndicators();

              // Apply rounded corners
              this.replaceTopStacksWithRoundedPath();
            }, 100);
          } else {
            // Just apply rounded corners if not all activities are visible
            setTimeout(() => {
              this.replaceTopStacksWithRoundedPath();
            }, 100);
          }
        }
      } catch (error) {
        console.error("Error updating chart series:", error);

        // If we encounter an error, rebuild the chart completely
        this.destroyChart();
        this.prepareChartOptions();
        this.updateFlag = true;
      }
    } else {
      // Fallback: If chart not available, rebuild everything
      this.prepareChartOptions();
      this.updateFlag = true;

      // Wait for chart to redraw and then apply rounded corners
      setTimeout(() => {
        // Only render goal icons if all activities are visible
        if (this.activityTypes.every((act) => act.visible)) {
          this.renderGoalAchievements();
        }
        this.replaceTopStacksWithRoundedPath();
      }, 300);
    }
  }

  /**
   * Apply SVG path with rounded corners to only the top element
   * This approach doesn't require any CSS
   */
  private replaceTopStacksWithRoundedPath(): void {
    if (!this.chart || !this.chart.series) {
      console.log("Chart not available for rounded corners");
      return;
    }

    try {
      // Process each stack position
      for (let xIndex = 0; xIndex < this.timeEntries.length; xIndex++) {
        // Find all visible column series
        const columnSeries = this.chart.series.filter(
          (s) =>
            s.visible &&
            s.options.type === "column" &&
            s.points &&
            s.points.length > xIndex
        );

        if (columnSeries.length === 0) {
          continue;
        }

        // Track the top point at this X position
        let topPoint = null;
        let topPointY = Number.MAX_VALUE;

        // Find top point in the stack
        columnSeries.forEach((series) => {
          const point = series.points[xIndex];
          if (point && point.y > 0 && point.graphic) {
            if (point.plotY < topPointY) {
              topPointY = point.plotY;
              topPoint = point;
            }
          }
        });

        // Apply rounded corners to the top element only
        if (topPoint && topPoint.graphic && topPoint.graphic.element) {
          const element = topPoint.graphic.element;

          // Only process rect elements
          if (element.tagName === "rect") {
            const rect = element;
            const x = parseFloat(rect.getAttribute("x") || "0");
            const y = parseFloat(rect.getAttribute("y") || "0");
            const width = parseFloat(rect.getAttribute("width") || "0");
            const height = parseFloat(rect.getAttribute("height") || "0");
            const fill = rect.getAttribute("fill") || "#000000";

            const radius = 5; // Corner radius

            // Create path with rounded top corners only
            const pathData = [
              `M ${x + radius} ${y}`, // Start at top-left + radius
              `L ${x + width - radius} ${y}`, // Line to top-right - radius
              `Q ${x + width} ${y} ${x + width} ${y + radius}`, // Top-right corner
              `L ${x + width} ${y + height}`, // Line to bottom-right
              `L ${x} ${y + height}`, // Line to bottom-left
              `L ${x} ${y + radius}`, // Line to top-left + radius
              `Q ${x} ${y} ${x + radius} ${y}`, // Top-left corner
              "Z", // Close path
            ].join(" ");

            // Create a new path element to replace the rect
            const path = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "path"
            );
            path.setAttribute("d", pathData);
            path.setAttribute("fill", fill);

            // Copy other attributes from rect to path
            for (let i = 0; i < rect.attributes.length; i++) {
              const attr = rect.attributes[i];
              if (
                attr.name !== "x" &&
                attr.name !== "y" &&
                attr.name !== "width" &&
                attr.name !== "height" &&
                attr.name !== "d"
              ) {
                path.setAttribute(attr.name, attr.value);
              }
            }

            // Replace rect with path
            if (rect.parentNode) {
              rect.parentNode.replaceChild(path, rect);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error applying rounded corners:", error);
    }
  }

  private isDateInRange(
    dateStr: string,
    startDate: Date,
    endDate: Date
  ): boolean {
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    return date >= start && date <= end;
  }

  /**
   * Process tracking data from API - updated to work with new data structure
   */
  private processTrackingData(): void {
    const data = this.trackingData;
    console.log(data);
    // Check for new data structure
    if (
      !data ||
      !data.userTimeSpent ||
      !data.userTimeSpent.timeSpentByPeriod ||
      !data.committedTimeMap
    ) {
      console.warn("Missing tracking data structure", { data });
      return;
    }

    // Determine date ranges based on the new structure
    let startDate: Date;
    let endDate: Date;

    // Get the current date from the data (most accurate)
    endDate = new Date(data.userTimeSpent.currentDate);

    // Calculate start date based on days input
    if (this.days === 15) {
      // Calculate 15 days back from current date
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 14); // 15 days including current day
    } else if (this.days === 30) {
      // Calculate 30 days back from current date
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 29); // 30 days including current day
    } else {
      // Default to 8 days, or use currentWeekStartDate if available
      if (data.userTimeSpent.currentWeekStartDate) {
        if (data?.weekType === "current") {
          startDate = new Date(data.userTimeSpent.currentWeekStartDate);
          endDate = new Date(data.userTimeSpent.currentWeekEndDate || endDate);
        } else {
          startDate = new Date(data.userTimeSpent.previousWeekStartDate);
          endDate = new Date(data.userTimeSpent.previousWeekEndDate || endDate);
        }
      } else {
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 7); // 8 days including current day
      }
    }

    // Create date range
    const dateRange = this.generateDateRange(startDate, endDate);

    // Reset data
    this.timeEntries = [];
    this.activityTypes = [];

    let totalStudyMinutes = 0;
    let datesWithData = 0;

    // Collect all activity types first
    const allActivityTypes = new Set<string>();

    // Process all dates in the timeSpentByPeriod to find all possible activity types
    Object.keys(data.userTimeSpent.timeSpentByPeriod).forEach((dateStr) => {
      const timeSpentData = data.userTimeSpent.timeSpentByPeriod[dateStr];

      if (
        timeSpentData &&
        timeSpentData.timeSpentByStage &&
        this.isDateInRange(dateStr, startDate, endDate)
      ) {
        // Iterate through stages
        Object.keys(timeSpentData.timeSpentByStage).forEach((stageName) => {
          // Iterate through modules in each stage
          const stageData = timeSpentData.timeSpentByStage[stageName];
          Object.keys(stageData).forEach((moduleName) => {
            // Check if this activity has significant time (meets threshold)
            const seconds = stageData[moduleName];
            const minutes = seconds / 60;
            const hours = this.convertMinutesToHours(minutes);

            // Only include activities that meet the threshold
            if (hours >= this.ACTIVITY_THRESHOLD) {
              // Create a combined activity name: "StageName - ModuleName"
              const activityName = `${stageName} - ${moduleName}`;
              allActivityTypes.add(activityName);
            }
          });
        });
      }
    });

    // Create activity types with unique colors
    this.activityTypes = Array.from(allActivityTypes).map(
      (activityName, index) => {
        return {
          id: activityName,
          name: activityName,
          color: this.getColorForIndex(index),
          visible: true, // All activities are visible by default
        };
      }
    );

    // Process the data for each date in the range
    this.timeEntries = dateRange.map((date) => {
      const dateStr = this.formatDate(date);
      const timeSpentData = data.userTimeSpent.timeSpentByPeriod[dateStr];
      const committedTimeSeconds = data.committedTimeMap[dateStr] || 0;

      // Count dates that have data
      if (timeSpentData && timeSpentData.timeSpentByStage) {
        datesWithData++;
      }

      const entry: TimeEntry = {
        date,
        activities: [],
        // Convert seconds to minutes (committedTimeMap still uses seconds)
        committedTimeMinutes: committedTimeSeconds / 60,
        goalAchieved: timeSpentData ? timeSpentData.goalAchieved : false,
      };

      // Add activities if data exists for this date
      if (timeSpentData && timeSpentData.timeSpentByStage) {
        // Iterate through stages
        Object.keys(timeSpentData.timeSpentByStage).forEach((stageName) => {
          // Iterate through modules in each stage
          const stageData = timeSpentData.timeSpentByStage[stageName];
          Object.keys(stageData).forEach((moduleName) => {
            // Get time in SECONDS and convert to minutes (values are in seconds in the API)
            const seconds = stageData[moduleName];
            const minutes = seconds / 60;
            const activityName = `${stageName} - ${moduleName}`;

            // Only add activities with significant time
            if (
              this.convertMinutesToHours(minutes) >= this.ACTIVITY_THRESHOLD
            ) {
              entry.activities.push({
                activityTypeId: activityName,
                minutes: minutes,
              });
              totalStudyMinutes += minutes;
            }
          });
        });
      }

      return entry;
    });

    this.totalStudyMinutes = totalStudyMinutes;
    this.CalculateTotalTimeSpent.emit(this.totalStudyMinutes);
    if (
      this.trackingData.progressStreak &&
      this.trackingData.progressStreak.weeklyProgress
    ) {
      const noStudyMap = {};

      // Create a map of date -> noStudyDate
      this.trackingData.progressStreak.weeklyProgress.forEach((dayData) => {
        const dateStr = dayData.date;
        noStudyMap[dateStr] = dayData.noStudyDate === true;
      });

      // Apply no-study flags to matching time entries
      this.timeEntries.forEach((entry) => {
        const dateStr = this.formatDate(entry.date);
        if (noStudyMap[dateStr] !== undefined) {
          entry.noStudyDate = noStudyMap[dateStr];
        }
      });
    }
  }

  /**
   * Generate array of dates between start and end
   */
  private generateDateRange(start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  /**
   * Format date as YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = this.padZero(date.getMonth() + 1);
    const day = this.padZero(date.getDate());
    return `${year}-${month}-${day}`;
  }

  private padZero(num: number): string {
    return num < 10 ? "0" + num : num.toString();
  }

  /**
   * Get a color from a predefined palette based on index
   */
  private getColorForIndex(index: number): string {
    const colors = [
      "#00AEEF",
      "#FFD200",
      "#00C5C5",
      "#FDA40A",
      "#D373FF",
      "#FF5C8D",
    ];

    return colors[index % colors.length];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.trackingData && !changes.trackingData.firstChange) ||
      (changes.days && !changes.days.firstChange)
    ) {
      // Store visibility preferences before processing new data
      const activityVisibilityMap = {};
      if (this.activityTypes && this.activityTypes.length > 0) {
        this.activityTypes.forEach((activity) => {
          activityVisibilityMap[activity.id] = activity.visible;
        });
      }

      // Process the new data
      this.processTrackingData();

      // Apply previous visibility settings to new activities
      if (Object.keys(activityVisibilityMap).length > 0) {
        this.activityTypes.forEach((activity) => {
          if (activityVisibilityMap[activity.id] !== undefined) {
            activity.visible = activityVisibilityMap[activity.id];
          }
        });
      }

      // Always destroy and recreate the chart for consistent behavior
      this.destroyChart();

      // Ensure chart container has proper dimensions
      this.ensureChartContainerDimensions();

      // Prepare new chart options
      this.prepareChartOptions();

      // Use a single consistent timeout for chart creation
      setTimeout(() => {
        if (this.chartContainer && this.chartContainer.nativeElement) {
          try {
            // Create a new chart instance
            const component = this;
            this.chart = Highcharts.chart(
              this.chartContainer.nativeElement,
              this.chartOptions,
              function () {
                component.chart = this;

                // Apply custom rendering in a single timeout after chart is ready
                setTimeout(() => {
                  if (component.activityTypes.every((act) => act.visible)) {
                    component.renderGoalAchievements();
                    component.renderNoStudyIndicators();
                  }
                  component.replaceTopStacksWithRoundedPath();
                }, 200);
              }
            );
          } catch (e) {
            console.error("Error creating chart:", e);
          }
        }
      }, 300);
    }
  }

  private ensureChartContainerDimensions(): void {
    if (this.chartContainer && this.chartContainer.nativeElement) {
      const container = this.chartContainer.nativeElement;
      const rect = container.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        container.style.width = "100%";
        container.style.height = "350px";
        container.style.minHeight = "350px";
      }
    }
  }

  /**
   * Prepare chart options from the time entries
   */
  private prepareChartOptions(): void {
    // Store reference to component for use in chart callbacks
    const component = this;

    // Make sure we have timeEntries before proceeding
    if (!this.timeEntries || this.timeEntries.length === 0) {
      console.error("No time entries available to prepare chart options");
      // Create empty chart options
      this.chartOptions = {
        chart: { type: "column", height: 350 },
        title: { text: "" },
        series: [],
      };
      return;
    }

    // Prepare date labels for the x-axis - show day of week
    const dateLabels = this.timeEntries.map((entry) => {
      const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
      return days[new Date(entry.date).getDay()];
    });

    // Create series data for the chart
    const series = this.prepareSeriesData();

    // Committed time line series
    const committedTimeData = this.timeEntries.map((entry) =>
      this.convertMinutesToHours(entry.committedTimeMinutes)
    );

    // Create the committed time series
    this.committedTimeSeries = {
      type: "line",
      name: "Committed Time",
      data: committedTimeData,
      color: "#FDA40A",
      lineWidth: 2,
      dashStyle: "ShortDash",
      marker: {
        enabled: true,
        fillColor: "#FDA40A",
        lineColor: "#fff",
        lineWidth: 3,
        radius: 7,
        symbol: "circle",
        // Add shadow to make the marker more visible
        states: {
          hover: {
            fillColor: "#FFC04A", // Slightly lighter color on hover
            radius: 8,
          },
        },
        style: {
          // Add a subtle drop shadow
          filter: "drop-shadow(0px 2px 3px rgba(0,0,0,0.3))",
        },
      },
      zIndex: 1,
    };

    // Add committed time series to both original and current series
    this.originalSeriesData.push({ ...this.committedTimeSeries });
    series.push(this.committedTimeSeries);

    // Calculate stacked totals for each day
    const stackedTotals = this.timeEntries.map((entry) => {
      const totalMinutes = entry.activities.reduce(
        (sum, act) => sum + act.minutes,
        0
      );
      return this.convertMinutesToHours(totalMinutes);
    });

    // Get max value considering both stacked totals and committed time
    let maxValue = 0;
    for (let i = 0; i < this.timeEntries.length; i++) {
      const stackedTotal = stackedTotals[i];
      const committedTime = this.convertMinutesToHours(
        this.timeEntries[i].committedTimeMinutes
      );
      const dayMax = Math.max(stackedTotal, committedTime);
      if (dayMax > maxValue) {
        maxValue = dayMax;
      }
    }

    // Round up to nearest 2 hours for clean y-axis divisions
    // Add extra space for the goal achievement icons
    let yAxisMax = Math.ceil(maxValue / 2) * 2 + 1; // Add extra padding

    // Ensure minimum yAxis height for better visibility
    yAxisMax = Math.max(yAxisMax, 4); // Minimum of 4 hours shown

    // Store goal achievement data for reference in the render event
    const goalAchievements = this.timeEntries.map(
      (entry) => entry.goalAchieved
    );

    // Create chart options
    this.chartOptions = {
      chart: {
        type: "column",
        height: 350,
        animation: true,
        reflow: true,
        spacingBottom: 5,
        spacingTop: 20,
        spacingLeft: 10,
        spacingRight: 1,
        style: {
          fontFamily: '"Gotham A", "Gotham B"',
        },
        // Get chart reference on load
        events: {
          load: function () {
            component.chart = this;
          },
          // Use the render event to add the checkmarks
          render: function () {
            const chart = this;

            setTimeout(() => {
              component.renderGoalAchievements();
              component.renderNoStudyIndicators();
            }, 50);

            // Add timeout to apply rounded corners after rendering completes
            setTimeout(() => {
              component.replaceTopStacksWithRoundedPath();
            }, 50);
          },
        },
      },
      title: {
        text: "", // No title
        style: {
          fontFamily: '"Gotham A", "Gotham B"',
        },
      },
      xAxis: {
        categories: dateLabels,
        crosshair: false,
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          style: {
            fontSize: component.days === 30 ? "10px" : "12px",
            fontFamily: '"Gotham A", "Gotham B"',
          },
          // Keep the text straight instead of tilted
          rotation: 0,
          // Format function to only show alternate dates in 30-day view
          formatter: function () {
            const chart = this.chart;
            const axis = this.axis;

            // For 30-day view, only show alternate labels
            if (component.days === 30 && axis.categories.length > 15) {
              // Show label only for even indices to display alternate days
              return this.pos % 2 === 0 ? this.value : "";
            }

            // For other views, show all labels
            return this.value;
          },
          // Add some space between labels and ensure they don't overlap
          padding: 5,
          // Ensure there's adequate spacing for labels
          step: component.days === 30 ? 1 : 0,
        },
        // Ensure there are enough ticks for all data points
        tickPositioner: function () {
          const positions = [];
          const tick = Math.floor(this.dataMax / 10);

          for (let i = 0; i <= this.dataMax; i++) {
            positions.push(i);
          }

          return positions;
        },
      },
      yAxis: {
        min: 0,
        max: yAxisMax,
        tickInterval: 2,
        title: {
          text: null, // No title
          style: {
            fontFamily: '"Gotham A", "Gotham B"',
          },
        },
        labels: {
          formatter: function () {
            return this.value + "h";
          },
          style: {
            fontFamily: '"Gotham A", "Gotham B"',
            fontSize: "12px",
          },
        },
        gridLineColor: "rgba(0, 0, 0, 0.1)",
      },
      tooltip: {
        formatter: this.getTooltipFormatter(),
        shared: true,
        useHTML: true,
        style: {
          fontFamily: '"Gotham A", "Gotham B"',
        },
        // Add these positioning properties
        followPointer: false,
        followTouchMove: false,
        // Add positioner function to ensure correct placement
        positioner: function (labelWidth, labelHeight, point) {
          // Calculate position based on point
          const chart = this.chart;
          const plotLeft = chart.plotLeft;
          const plotTop = chart.plotTop;

          // Get the x position in the middle of the point
          let x = point.plotX + plotLeft - labelWidth / 2;

          // Make sure tooltip doesn't go outside chart area on the left
          x = Math.max(x, plotLeft + 10);

          // Make sure tooltip doesn't go outside chart area on the right
          x = Math.min(x, chart.chartWidth - labelWidth - 10);

          // Position tooltip above the point
          let y = point.plotY + plotTop - labelHeight - 10;

          // If tooltip would go off the top, position it below the point
          if (y < plotTop) {
            y = point.plotY + plotTop + 20;
          }

          return { x, y };
        },
        // Prevent weird CSS positioning
        outside: false,
        hideDelay: 100,
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadius: 0, // Reset all border radius settings
          pointPadding: 0.1,
          groupPadding: 0.2,
        },
        series: {
          animation: true,
          dataLabels: {
            style: {
              fontFamily: '"Gotham A", "Gotham B"',
            },
          },
          events: {
            legendItemClick: function () {
              // Prevent toggling visibility through legend clicks
              return false;
            },
          },
          states: {
            inactive: {
              opacity: 1, // Keep inactive series fully opaque
            },
            hover: {
              brightness: 0.1, // Reduce brightness change on hover
              halo: {
                size: 0, // Remove halo effect
              },
            },
          },
        },
      },
      legend: {
        enabled: false, // We'll use our custom legend outside the chart
        itemStyle: {
          fontFamily: '"Gotham A", "Gotham B"',
        },
      },
      credits: {
        enabled: false,
      },
      series: series,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              xAxis: {
                labels: {
                  rotation: 45, // On smaller screens, allow rotation if needed
                  align: "right",
                },
              },
            },
          },
        ],
      },
    };

    // IMPORTANT: Force chart creation if chart container is available and we're switching day ranges
    if (this.chartContainer && this.chartContainer.nativeElement) {
      try {
        const containerEl = this.chartContainer.nativeElement;
        const rect = containerEl.getBoundingClientRect();

        // Only force creation if container has valid dimensions
        if (rect.width > 0 && rect.height > 0) {
          // Create a new chart directly for the most reliable update
          setTimeout(() => {
            try {
              // Only create directly if we don't already have an update flag set
              if (!this.updateFlag) {
                this.chart = Highcharts.chart(containerEl, this.chartOptions);
              }
            } catch (e) {
              console.error("Error in direct chart creation:", e);
            }
          }, 0);
        } else {
          console.warn(
            `Invalid container dimensions: ${rect.width}x${rect.height}`
          );

          // Force the container to have dimensions
          containerEl.style.height = "350px";
          containerEl.style.width = "100%";
          containerEl.style.minHeight = "350px";
        }
      } catch (error) {
        console.error("Error checking chart container:", error);
      }
    }
    // this.chartOptions.responsive = {
    //   rules: [{
    //     condition: {
    //       maxWidth: 500
    //     },
    //     chartOptions: {
    //       xAxis: {
    //         labels: {
    //           rotation: 45, // On smaller screens, allow rotation if needed
    //           align: 'right'
    //         }
    //       }
    //     }
    //   }]
    // };
  }

  private convertMinutesToHours(minutes: number): number {
    if (!minutes || isNaN(minutes)) {
      return 0;
    }

    // Use more decimal places for small values
    const hours = minutes / 60;

    // Use 2 decimal places for very small times to prevent rounding to zero
    if (minutes < 6) {
      return parseFloat(hours.toFixed(2));
    }

    return parseFloat(hours.toFixed(1));
  }

  /**
   * Get display value for chart rendering
   * Separate function for visual representation that applies minimum height
   */
  private getDisplayValueForChart(minutes: number): number {
    if (!minutes || isNaN(minutes)) {
      return 0;
    }

    // Apply minimum visual height for very small values
    if (minutes > 0 && minutes < 6) {
      return 0.15; // Minimum visual height
    }

    // Otherwise use normal conversion
    return this.convertMinutesToHours(minutes);
  }

  /**
   * Prepare series data with minimum height for small values
   */
  private prepareSeriesData(): any[] {
    const series: any[] = [];
    this.originalSeriesData = []; // Reset original data

    // Activity bars series (stacked)
    this.activityTypes.forEach((activityType) => {
      // Prepare data array with exact zeros for non-significant values
      const data = this.timeEntries.map((entry) => {
        const activity = entry.activities.find(
          (a) => a.activityTypeId === activityType.id
        );

        if (!activity) return 0;

        // Use getDisplayValueForChart for visual representation only
        const displayValue = this.getDisplayValueForChart(activity.minutes);

        // Ensure any value below threshold is exactly 0, not a tiny value
        return displayValue >= this.ACTIVITY_THRESHOLD ? displayValue : 0;
      });

      // Check if this activity has any significant data
      const hasSignificantData = data.some(
        (value) => value >= this.ACTIVITY_THRESHOLD
      );

      // Only include activities with significant data
      if (hasSignificantData) {
        const seriesConfig = {
          name: activityType.name,
          type: "column",
          data,
          color: activityType.color,
          borderWidth: 0,
          stack: "stack1",
          visible: activityType.visible,
        };

        // Add to both original and current series
        this.originalSeriesData.push({ ...seriesConfig });
        series.push(seriesConfig);
      }
    });

    return series;
  }

  private getTooltipFormatter() {
    const component = this;

    return function() {
      // Safety check
      if (!this || !this.points) {
        return 'No data available';
      }

      // Get the current point's index
      const pointIndex = this.points[0] ? this.points[0].point.index : null;

      // Safety check
      if (pointIndex === null || pointIndex === undefined) {
        return 'No data available';
      }

      // Find the actual time entry
      const timeEntry = component && component.timeEntries && pointIndex < component.timeEntries.length
        ? component.timeEntries[pointIndex]
        : null;

      // Get day label
      const dayLabel = this.points[0] && this.points[0].series.xAxis
        ? this.points[0].series.xAxis.categories[pointIndex]
        : 'Date';

      let tooltipHtml = `<span style="font-size: 10px">${dayLabel}</span><br/>`;

      // Handle committed time
      const committedTimeSeries = this.points.find(point =>
        point.series && point.series.name === 'Committed Time'
      );

      if (committedTimeSeries) {
        tooltipHtml += `<span style="color:${committedTimeSeries.color}">●</span> Committed Time: <b>${committedTimeSeries.y}h</b><br/>`;
      }

      // Get activity points
      const activityPoints = this.points.filter(point =>
        point.series && point.series.name !== 'Committed Time' && point.y > 0
      );

      // Sort by size
      activityPoints.sort((a, b) => b.y - a.y);

      // Add activities to tooltip
      activityPoints.forEach(point => {
        if (!point.series || !point.series.name) {
          return;
        }

        const activityName = point.series.name;

        if (!component || !component.activityTypes) {
          tooltipHtml += `<span style="color:${point.color}">●</span> ${activityName}: <b>${point.y}h</b><br/>`;
          return;
        }

        const activityType = component.activityTypes.find(type => type.name === activityName);

        if (activityType && timeEntry) {
          // Find actual activity data
          const activity = timeEntry.activities.find(a => a.activityTypeId === activityType.id);

          if (activity) {
            // Use convertMinutesToHours for accurate data display
            const actualHours = component.convertMinutesToHours(activity.minutes);

            // Show accurate hours (not the enhanced display value)
            tooltipHtml += `<span style="color:${point.color}">●</span> ${activityName}: <b>${actualHours}h</b><br/>`;
          } else {
            tooltipHtml += `<span style="color:${point.color}">●</span> ${activityName}: <b>${point.y}h</b><br/>`;
          }
        } else {
          tooltipHtml += `<span style="color:${point.color}">●</span> ${activityName}: <b>${point.y}h</b><br/>`;
        }
      });

      return tooltipHtml;
    };
  }
}
